import {CONVERSATIONS_DIRECTORY,LIBRARY_CONVERSATIONS_ID,type FileRecord,type LibraryDirectoryPath,type LibraryFolder,type LibraryItemRef,type LibrarySnapshot,type ThreadRecord} from '@reados/contracts';

export type LibraryEntry = {id:string;kind:'file';name:string;file:FileRecord}|{id:string;kind:'thread';name:string;thread:ThreadRecord}|{id:string;kind:'folder';name:string;folder:LibraryFolder};
export const LIBRARY_DRAG_TYPE='application/x-reados-library-entry';
export const libraryKey=(entry:LibraryItemRef)=>`${entry.kind}:${entry.id}`;
export const sameLibraryEntry=(a:LibraryItemRef,b:LibraryItemRef)=>a.kind===b.kind&&a.id===b.id;
export const libraryRef=(entry:LibraryItemRef):LibraryItemRef=>({kind:entry.kind,id:entry.id});

/** Compatibility for legacy records; a persisted snapshot is authoritative once present. */
export function threadDirectoryPath(thread:ThreadRecord):LibraryDirectoryPath {
  return thread.directoryPath===CONVERSATIONS_DIRECTORY?CONVERSATIONS_DIRECTORY:'/';
}
export function libraryEntries(files:FileRecord[],threads:ThreadRecord[],directoryPath:LibraryDirectoryPath):LibraryEntry[] {
  return [
    ...(directoryPath==='/'?files.map(file=>({id:file.id,kind:'file' as const,name:file.name,file})):[]),
    ...threads.filter(thread=>threadDirectoryPath(thread)===directoryPath).map(thread=>({id:thread.id,kind:'thread' as const,name:thread.title||'新对话',thread})),
  ].sort((a,b)=>a.name.localeCompare(b.name,'zh-CN',{numeric:true}));
}
export function libraryParent(snapshot:LibrarySnapshot|null|undefined,entry:LibraryItemRef,threads:ThreadRecord[]=[]):string|null {
  const location=snapshot?.locations.find(item=>sameLibraryEntry(item,entry));
  if(location)return location.parentId;
  return entry.kind==='thread'&&threads.find(thread=>thread.id===entry.id)?.directoryPath===CONVERSATIONS_DIRECTORY?LIBRARY_CONVERSATIONS_ID:null;
}
export function libraryFolders(snapshot:LibrarySnapshot|null|undefined):LibraryFolder[]{
  return snapshot?.folders??[{id:LIBRARY_CONVERSATIONS_ID,name:'Conversations',system:'conversations',createdAt:'',relativePath:'Conversations',sandboxPath:'/workspace/Conversations'}];
}
export function libraryChildren(files:FileRecord[],threads:ThreadRecord[],snapshot:LibrarySnapshot|null|undefined,parentId:string|null):LibraryEntry[]{
  const entries:LibraryEntry[]=[...files.map(file=>({kind:'file' as const,id:file.id,name:file.name,file})),...threads.map(thread=>({kind:'thread' as const,id:thread.id,name:thread.title||'新对话',thread})),...libraryFolders(snapshot).map(folder=>({kind:'folder' as const,id:folder.id,name:folder.name,folder}))];
  const positions=new Map(snapshot?.locations.map(item=>[libraryKey(item),item.position]));
  return entries.filter(entry=>libraryParent(snapshot,entry,threads)===parentId).sort((a,b)=>{
    const aPosition=positions.get(libraryKey(a)),bPosition=positions.get(libraryKey(b));
    if(aPosition!==undefined||bPosition!==undefined)return (aPosition??Number.MAX_SAFE_INTEGER)-(bPosition??Number.MAX_SAFE_INTEGER)||libraryKey(a).localeCompare(libraryKey(b));
    if(a.kind==='folder'&&a.id===LIBRARY_CONVERSATIONS_ID)return -1;
    if(b.kind==='folder'&&b.id===LIBRARY_CONVERSATIONS_ID)return 1;
    return a.name.localeCompare(b.name,'zh-CN',{numeric:true});
  });
}
export function libraryBreadcrumbs(snapshot:LibrarySnapshot|null|undefined,parentId:string|null):LibraryFolder[]{
  const result:LibraryFolder[]=[],seen=new Set<string>();
  for(let id=parentId;id&&!seen.has(id);){
    seen.add(id);const folder=libraryFolders(snapshot).find(item=>item.id===id);if(!folder)break;
    result.unshift(folder);id=libraryParent(snapshot,{kind:'folder',id});
  }
  return result;
}
export function canMoveLibraryEntry(snapshot:LibrarySnapshot|null|undefined,entry:LibraryItemRef,parentId:string|null):boolean{
  if(entry.kind!=='folder')return parentId===null||libraryFolders(snapshot).some(folder=>folder.id===parentId);
  if(entry.id===LIBRARY_CONVERSATIONS_ID)return parentId===null;
  if(parentId!==null&&!libraryFolders(snapshot).some(folder=>folder.id===parentId))return false;
  const seen=new Set<string>();
  for(let id=parentId;id!==null;){
    if(id===entry.id||seen.has(id))return false;
    seen.add(id);id=libraryParent(snapshot,{kind:'folder',id});
  }
  return true;
}
export type LibraryDropZone='before'|'after'|'inside';
export function libraryDropZone(kind:LibraryEntry['kind'],fraction:number):LibraryDropZone{
  return kind==='folder'&&fraction>=.25&&fraction<=.75?'inside':fraction<.5?'before':'after';
}
/** Resolve after to a stable successor identity, excluding the dragged row itself. */
export function libraryInsertion(entries:LibraryEntry[],dragged:LibraryItemRef,target:LibraryItemRef,zone:'before'|'after'):LibraryItemRef|null{
  const remaining=entries.filter(entry=>!sameLibraryEntry(entry,dragged));
  const index=remaining.findIndex(entry=>sameLibraryEntry(entry,target));
  const before=remaining[index+(zone==='after'?1:0)];
  return index<0||!before?null:libraryRef(before);
}
/** Catalog responses may finish out of order; revisions never move backwards. */
export function newerLibrarySnapshot(current:LibrarySnapshot|null,next:LibrarySnapshot):LibrarySnapshot{
  return current&&current.revision>next.revision?current:next;
}
